import { useEffect, useRef } from 'react'
import moment from 'moment'
import { eventLinkGenerator } from 'config/notificationsLinksMapping'
import { RiGoogleLine, RiRobotLine, RiUser3Line } from 'react-icons/ri'
import { TbBriefcase2 } from 'react-icons/tb'
import { GoGlobe } from 'react-icons/go'
import Bell01 from '@untitled-ui/icons-react/build/esm/Bell01'
import Check from '@untitled-ui/icons-react/build/esm/Check'
import {
	AppButton,
	DotMatrix,
	NotificationItem,
	NotificationPanel,
	Tooltip
} from '@clickguard/design-system'
import { NavLink } from 'react-router-dom'
import { useAssetStore, useUiStore } from 'store'
import { useTrackPostHogEvent } from 'utils/posthog'
import { NotificationDTO } from 'api-models'
import { DropdownNotificationsProps } from './types'
import { PostHogEvent, PostHogPath } from '../../../utils/posthog-events'

const contextIcon = {
	USER: RiUser3Line,
	ACCOUNT: TbBriefcase2,
	CAMPAIGN: TbBriefcase2,
	WEBSITE: GoGlobe,
	SYSTEM: RiRobotLine
}

const chooseIcon = (data: NotificationDTO) => {
	if (data.context_type === 'PPC_ACCOUNT') return RiGoogleLine
	return contextIcon[data.context_type as keyof typeof contextIcon]
}

export const DropdownNotifications = (props: DropdownNotificationsProps) => {
	const {
		setOpen,
		dropdownOpen,
		setDropdownOpen,
		state,
		onReadAllNotification,
		notificationId
	} = props
	const isLoading = useUiStore((s) => s.isLoading)
	const { demoMode } = useAssetStore()
	const trackPostHogEvent = useTrackPostHogEvent()

	const containerRef = useRef<HTMLDivElement>(null)
	const showNotificationAlert = state.notifications.length > 0
	const unreadCount = state.notifications.filter((n) => !n.read).length

	useEffect(() => {
		if (!dropdownOpen) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setDropdownOpen(false)
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setDropdownOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [dropdownOpen, setDropdownOpen])

	const handleToggle = () => {
		const next = !dropdownOpen
		setDropdownOpen(next)
		if (next) {
			trackPostHogEvent(
				PostHogEvent.HEADER_NOTIFICATIONS,
				PostHogPath.GLOBAL,
				null
			)
		}
	}

	const notificationsButton = (
		<button
			aria-label="Notifications"
			onClick={handleToggle}
			className="relative flex items-center justify-center p-1.5 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
		>
			<span className="relative inline-block size-6 leading-none">
				<Bell01 className="size-6 text-t-desc-gray" />
				{showNotificationAlert && (
					<span
						aria-hidden="true"
						className="absolute top-0 right-[3px] w-[8px] h-[8px] rounded-[4px] bg-t-danger pointer-events-none"
					/>
				)}
			</span>
		</button>
	)

	const markAllButton = (
		<AppButton
			loader="dots"
			variant="ghost"
			size="mini"
			label="Mark all as read"
			leftIcon={<Check className="size-4" />}
			disabled={demoMode}
			isLoading={isLoading('READ_NOTIFICATIONS')}
			onClick={() => onReadAllNotification({ read: 'false' })}
		/>
	)

	return (
		<div className="relative h-10 flex items-center" ref={containerRef}>
			{/* Drop the hover tooltip while the notifications dropdown is open so it
			   doesn't linger over the menu (review pin #0029). The DS Tooltip is
			   hover-only with no controlled-open prop, so we unwrap instead.
			   Gated on this menu's own `dropdownOpen` (not the shared
			   headerPopover): the bell lives inside its own container, so toggling
			   it never remounts the trigger mid-click. */}
			{dropdownOpen ? (
				notificationsButton
			) : (
				<Tooltip content="Notifications" position="bottom">
					{notificationsButton}
				</Tooltip>
			)}

			{dropdownOpen && (
				<div className="absolute right-0 top-full mt-2 w-[432px] z-50">
					<NotificationPanel
						count={unreadCount}
						countMax={99}
						action={
							demoMode ? (
								<Tooltip
									content="This action is disabled when demo data is displayed"
									position="top"
								>
									{markAllButton}
								</Tooltip>
							) : (
								markAllButton
							)
						}
						footer={
							<AppButton
								variant="secondary"
								fullWidth
								label="View all notifications"
								onClick={() => {
									setDropdownOpen(false)
									setOpen(true)
								}}
							/>
						}
					>
						{state.notifications.length === 0 ? (
							<div className="flex items-center justify-center py-8 text-[14px] text-t-gray-hold">
								No new notifications
							</div>
						) : (
							<div className="max-h-[312px] overflow-y-auto overflow-x-hidden">
								{state.notifications.map((n) => {
									const Icon = chooseIcon(n)
									return (
										<NotificationItem
											key={n.id}
											as={NavLink}
											to={demoMode ? '#' : eventLinkGenerator(n)}
											icon={Icon && <Icon className="size-4" />}
											title={n.message}
											description={n.context_name}
											timestamp={moment(n.created_at).fromNow()}
											unread={!n.read}
											trailing={
												n.id === notificationId ? (
													<DotMatrix size="2xs" label="Marking as read" />
												) : undefined
											}
										/>
									)
								})}
							</div>
						)}
					</NotificationPanel>
				</div>
			)}
		</div>
	)
}
